"use client";

import { TimingCitySelector } from "@/components/choghadiya/ChoghadiyaCitySelector";
import type { MuhuratPurpose, ShubhMuhuratResponse } from "@/lib/api/muhurat";
import { ShubhMuhuratDigest } from "./ShubhMuhuratDigest";
import type { MuhuratVerdict } from "./shubh-muhurat-verdict";
import { useShubhMuhurat } from "./use-shubh-muhurat";

const PURPOSE_OPTIONS: Array<{ value: MuhuratPurpose; label: string }> = [
  { value: "general", label: "Important beginning" },
  { value: "career", label: "Career or job joining" },
  { value: "business", label: "Business or signing" },
  { value: "marriage", label: "Marriage or engagement" },
  { value: "travel", label: "Travel or departure" },
  { value: "meditation", label: "Meditation or puja" },
];

function PurposePicker({
  purpose,
  onChange,
}: {
  purpose: MuhuratPurpose;
  onChange: (purpose: MuhuratPurpose) => void;
}) {
  return (
    <label className="mx-auto mt-4 block max-w-sm text-left">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ish-text-muted">
        What are you starting?
      </span>
      <select
        value={purpose}
        onChange={(event) => onChange(event.target.value as MuhuratPurpose)}
        className="input-sacred w-full rounded-lg px-3 py-2 text-sm text-ish-dark"
      >
        {PURPOSE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function VerdictCard({ verdict }: { verdict: MuhuratVerdict }) {
  const styles = {
    good: "border-emerald-200 bg-emerald-50/70 text-emerald-900",
    wait: "border-amber-200 bg-amber-50/70 text-amber-950",
    neutral: "border-ish-border-light bg-ish-surface text-ish-dark",
  } as const;
  const labels = { good: "Good time now", wait: "Avoid window active", neutral: "Timing check" };

  return (
    <div className={`rounded-xl border p-4 ${styles[verdict.tone]}`} aria-live="polite">
      <p className="text-xs font-semibold uppercase tracking-widest">{labels[verdict.tone]}</p>
      <p className="mt-1 text-lg font-bold">{verdict.heading}</p>
      <p className="mt-1 text-sm leading-relaxed opacity-80">{verdict.detail}</p>
    </div>
  );
}

function ToolSkeleton() {
  return (
    <div className="space-y-3" aria-label="Calculating today's shubh muhurat">
      <div className="h-28 animate-pulse rounded-xl bg-stone-200/70" />
      <div className="h-44 animate-pulse rounded-xl bg-stone-200/70" />
    </div>
  );
}

export default function ShubhMuhuratTool({
  initialDigest,
  initialPreviousNightDigest = null,
}: {
  initialDigest: ShubhMuhuratResponse | null;
  initialPreviousNightDigest?: ShubhMuhuratResponse | null;
}) {
  const { city, digest, error, loading, purpose, retry, selectCity, selectPurpose, verdict } =
    useShubhMuhurat(initialDigest, initialPreviousNightDigest);

  return (
    <section className="intent-interface-timing-first min-h-[calc(100vh-130px)]">
      <div className="card-sacred p-5 sm:p-6">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-intent-brand">
            Live India timing check
          </p>
          <h2 className="mt-2 font-serif text-2xl font-bold text-ish-dark">
            Can I start something important now?
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-ish-text-muted">
            Choose an Indian city. The result uses today&apos;s local sunrise, Panchang and
            Choghadiya windows—not a fixed national timetable.
          </p>
          <TimingCitySelector city={city} onCitySelect={selectCity} countryFilter="IN" />
          <PurposePicker purpose={purpose} onChange={selectPurpose} />
        </div>

        <div className="mt-5">
          {loading && <ToolSkeleton />}
          {error && !loading && (
            <div className="rounded-xl border border-red-200 bg-red-50/70 p-5 text-center">
              <p className="text-sm text-red-700">{error}</p>
              <button
                type="button"
                onClick={retry}
                className="mt-3 text-sm font-semibold text-intent-brand"
              >
                Try again
              </button>
            </div>
          )}
          {digest && !loading && !error && (
            <div className="space-y-5">
              {verdict ? (
                <VerdictCard verdict={verdict} />
              ) : (
                <p className="text-center text-sm text-ish-text-muted">
                  Checking the current India time…
                </p>
              )}
              <ShubhMuhuratDigest digest={digest} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
